<template>
  <div class="cs-page" data-testid="chat-simulator" :data-theme="theme">
    <div class="cs-shell" data-testid="chat-shell">
    <header class="cs-header" data-testid="chat-header">
      <div class="cs-avatar"><i class="fa fa-user"></i></div>
      <div class="cs-title">
        <span class="cs-name">Chat Simulator</span>
        <small class="cs-sub" data-testid="chat-chat-id">{{ chatIdLabel }}</small>
      </div>
      <div class="cs-controls">
        <label class="cs-label" for="cs-country">Country</label>
        <select
          id="cs-country"
          class="cs-select"
          data-testid="chat-country-select"
          v-model="selectedCountryId"
          :disabled="loadingCountries">
          <option value="" disabled>{{ loadingCountries ? 'Loading...' : 'Select country' }}</option>
          <option
            v-for="c in countries"
            :key="c.id"
            :value="c.id"
            :data-country-id="c.id">
            {{ c.name }}
          </option>
        </select>
        <button type="button" class="cs-icon-btn" data-testid="chat-theme-btn" title="Switch to light/dark theme" :aria-label="theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'" @click="toggleTheme">
          <svg v-if="theme === 'dark'" class="cs-svg" data-testid="chat-theme-icon-sun" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="4.5" fill="currentColor" stroke="none"/><path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8"/></svg>
          <svg v-else class="cs-svg" data-testid="chat-theme-icon-moon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20.5 14.6A8.5 8.5 0 0 1 9.4 3.5a.7.7 0 0 0-1-.8A10 10 0 1 0 21.3 15.6a.7.7 0 0 0-.8-1z"/></svg>
        </button>
        <button type="button" class="cs-icon-btn" data-testid="chat-new-btn" title="New chat (starts as a new user)" aria-label="New chat" :disabled="!selectedCountry || demo" @click="newChat">
          <i class="fa fa-user-plus"></i>
        </button>
        <button type="button" class="cs-icon-btn" data-testid="chat-restart-btn" title="Restart chat" @click="restart">
          <i class="fa fa-refresh"></i>
        </button>
      </div>
    </header>

    <div v-if="countryError" class="cs-error" data-testid="chat-country-error">
      {{ countryError }}
    </div>

    <div v-if="sendError" class="cs-error" data-testid="chat-send-error">{{ sendError }}</div>
    <div v-if="!demo && selectedCountry && !streamOnline" class="cs-notice" data-testid="chat-notice">
      Connecting to replies...
    </div>

    <main class="cs-thread" data-testid="chat-thread" ref="thread">
      <div v-if="loadingHistory" class="cs-empty" data-testid="chat-history-loading">Loading chat...</div>
      <div v-else-if="!messages.length" class="cs-empty" data-testid="chat-empty">{{ demo ? 'No messages yet.' : (selectedCountry ? 'Send a journey keyword to start.' : 'Select a country to open its chat.') }}</div>
      <message-bubble
        v-for="m in messages"
        :key="m.msgId"
        :message="m"
        :disabled="!demo && m.msgId !== activeId"
        @reply="onReply"
        @location="onLocation" />
      <div v-if="typing" class="cs-typing" data-testid="chat-typing">
        <span></span><span></span><span></span>
      </div>
    </main>

    <!-- attachment preview -->
    <div v-if="pending" class="cs-pending" data-testid="chat-attachment-preview">
      <img v-if="pending.kind === 'image'" class="cs-pending-thumb" :src="pending.url" alt="preview">
      <video v-else-if="pending.kind === 'video'" class="cs-pending-thumb" :src="pending.url" muted></video>
      <i v-else class="fa fa-file-o cs-pending-icon"></i>
      <div class="cs-pending-meta">
        <span class="cs-pending-name" data-testid="chat-attachment-name">{{ pending.name }}</span>
        <small>{{ pending.sizeLabel }}</small>
      </div>
      <button type="button" class="cs-icon-btn" data-testid="chat-attachment-remove" title="Remove" @click="clearPending">
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div v-if="attachError" class="cs-error" data-testid="chat-attachment-error">{{ attachError }}</div>

    <!-- emoji picker -->
    <div v-if="emojiOpen" class="cs-emoji" data-testid="chat-emoji-picker" @click.stop>
      <button
        v-for="e in emojis"
        :key="e"
        type="button"
        class="cs-emoji-btn"
        :data-emoji="e"
        @click="insertEmoji(e)">{{ e }}</button>
    </div>

    <footer class="cs-footer" data-testid="chat-footer">
      <input
        type="file"
        ref="fileInput"
        class="cs-file"
        data-testid="chat-file-input"
        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        @change="onFilePicked">
      <button type="button" class="cs-footer-icon" data-testid="chat-attach-btn" title="Attach" @click="$refs.fileInput.click()">
        <i class="fa fa-paperclip"></i>
      </button>
      <button type="button" class="cs-footer-icon" data-testid="chat-emoji-btn" title="Emoji" @click.stop="emojiOpen = !emojiOpen">
        <i class="fa fa-smile-o"></i>
      </button>
      <input
        type="text"
        ref="input"
        class="cs-input"
        data-testid="chat-input"
        :placeholder="pending ? 'Add a caption' : 'Type a message'"
        autocomplete="off"
        v-model="draft"
        @keyup.enter="send">
      <button
        type="button"
        class="cs-send"
        data-testid="chat-send-btn"
        :title="canSend ? 'Send' : 'Voice message (not available)'"
        :disabled="!canSend"
        @click="send">
        <i class="fa" :class="canSend ? 'fa-paper-plane' : 'fa-microphone'"></i>
      </button>
    </footer>
    </div>
  </div>
</template>

<script>
import api from '../../../utils/api'
import MessageBubble from './MessageBubble.vue'
import sampleMessages from './sampleMessages'
import openChatStream from './chatStream'

// Sends the user's messages to the journey service through POST /api/chat/message and shows
// the replies as they arrive on the stream (GET /api/chat/stream). The earlier part of the chat
// is loaded from GET /api/chat/history. Open with ?demo=1 to see the sample conversation,
// in that mode nothing is sent.
const TYPING_MAX_MS = 25000
// Messages use the Turn.io payload shape. See docs/chat-interface/02-api-contract.md
const MAX_FILE_BYTES = 16 * 1024 * 1024
let localSeq = 0
const nextId = () => 'local-' + Date.now() + '-' + (++localSeq)

export default {
  name: 'ChatSimulator',
  components: { MessageBubble },
  data () {
    return {
      countries: [],
      selectedCountryId: '',
      loadingCountries: false,
      countryError: '',
      demo: this.$route && this.$route.query && this.$route.query.demo === '1',
      messages: [],
      sendError: '',
      loadingHistory: false,
      streamOnline: false,
      liveSeq: 0,
      generation: 0,
      draft: '',
      typing: false,
      theme: 'dark',
      pending: null,
      attachError: '',
      emojiOpen: false,
      emojis: ['😀', '😁', '😂', '🤣', '😊', '😍', '😘', '😎', '🤔', '😅', '😢', '😭', '😡', '🥳', '🙏', '👍', '👎', '👏', '🙌', '💪', '👋', '🤝', '✌️', '👌', '❤️', '💔', '🔥', '⭐', '🎉', '✅', '❌', '⚠️', '🤰', '👶', '💉', '🏥', '💊', '🩺', '🌸', '🌞', '🍎', '💧', '📍', '📞', '📅', '⏰', '💬', '👩', '👨', '👧', '👦']
    }
  },
  computed: {
    selectedCountry () {
      return this.countries.find(c => c.id === this.selectedCountryId) || null
    },
    // buttons are only usable on the last message, and only when it came from the journey
    activeId () {
      const last = this.messages[this.messages.length - 1]
      return last && last.direction === 'out' ? last.msgId : null
    },
    canSend () {
      return !!(this.draft.trim() || this.pending)
    },
    chatIdLabel () {
      if (!this.selectedCountry) return 'no country selected'
      return this.generation ? `${this.selectedCountry.name} · new chat ${this.generation}` : this.selectedCountry.name
    }
  },
  watch: {
    // every country is its own chat (own history, stream and journey), so changing it opens that chat
    selectedCountryId () {
      this.switchChat()
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.closeEmoji)
    this.clearPending()
    this.closeStream()
    this.stopTyping()
  },
  mounted () {
    document.addEventListener('click', this.closeEmoji)
    try {
      const saved = window.localStorage.getItem('chatSimulatorTheme')
      if (saved === 'light' || saved === 'dark') this.theme = saved
    } catch (e) {}
    if (this.demo) {
      this.messages = sampleMessages.map(m => Object.assign({ status: null }, m))
    }
    this.loadCountries()
    this.scrollToBottom()
  },
  methods: {
    async loadCountries () {
      this.loadingCountries = true
      this.countryError = ''
      try {
        const res = await api.buildRequest('getAllSupportedCountries', 'POST', null)
        if (res.status === 200) {
          const list = Array.isArray(res.data) ? res.data : (res.data && res.data.payload ? res.data.payload : [])
          this.countries = list
            .map(c => ({ id: c.id, name: c.full_name || c.name }))
            .filter(c => c.name)
            .sort((a, b) => a.name.localeCompare(b.name))
          if (this.countries.length === 1) this.selectedCountryId = this.countries[0].id
          else if (!this.demo) await this.selectServerCountry()
        } else {
          this.countryError = 'Could not load countries.'
        }
      } catch (e) {
        this.countryError = 'Could not load countries.'
      } finally {
        this.loadingCountries = false
      }
    },
    // Starts on the country this dashboard server belongs to (India for ap-south-1)
    async selectServerCountry () {
      try {
        const res = await api.buildRequest('getEnv', 'GET')
        const name = res.status === 200 && res.data && res.data.country
        const match = name && this.countries.find(c => c.name.toLowerCase() === String(name).toLowerCase())
        if (match) this.selectedCountryId = match.id
      } catch (e) {
        // the user can still pick a country
      }
    },
    // Opens the chat of the selected country: empty thread, new stream, then its history
    switchChat () {
      if (this.demo) return
      this.closeStream()
      this.stopTyping()
      this.liveSeq += 1
      this.messages = []
      this.sendError = ''
      this.streamOnline = false
      this.loadingHistory = false
      this.generation = this.readGeneration()
      if (this.selectedCountry) this.startLive(this.selectedCountry.name)
    },
    // The "new chat" number is kept per country in this browser, each number is a separate chat and user
    generationKey () {
      return 'chatSimulatorGeneration:' + (this.selectedCountry ? this.selectedCountry.name.toLowerCase() : '')
    },
    readGeneration () {
      if (!this.selectedCountry) return 0
      try {
        const value = parseInt(window.localStorage.getItem(this.generationKey()), 10)
        return value > 0 && value <= 999 ? value : 0
      } catch (e) {
        return 0
      }
    },
    // Starts a brand-new chat for this country: a new id, so the system treats it as a new user
    newChat () {
      if (this.demo || !this.selectedCountry) return
      if (!window.confirm('Start a new chat as a new user? This chat stays in the database but is no longer shown here.')) return
      const next = Math.min(this.generation + 1, 999)
      try {
        window.localStorage.setItem(this.generationKey(), String(next))
      } catch (e) {
        this.sendError = 'Could not save the new chat in this browser.'
        return
      }
      this.switchChat()
    },
    // Loads the earlier part of this user's chat (from the message logs), so a reload keeps the thread
    async loadHistory (country, seq, generation) {
      this.loadingHistory = true
      try {
        const res = await api.buildRequest('chat/history', 'GET', { country, generation: generation || 0, limit: 100 })
        // the country was changed while this was loading: the answer belongs to another chat
        if (seq !== this.liveSeq) return
        if (res.status === 200 && res.data && res.data.payload && Array.isArray(res.data.payload.messages)) {
          this.mergeHistory(res.data.payload.messages)
        } else {
          this.sendError = 'Could not load the earlier chat.'
        }
      } catch (e) {
        if (seq === this.liveSeq) this.sendError = 'Could not load the earlier chat.'
      } finally {
        if (seq === this.liveSeq) this.loadingHistory = false
      }
    },
    // Live part: connect to the stream first, then load the history, so nothing falls in between.
    // Messages are matched by msgId, so a message that is in both is only shown once.
    startLive (country) {
      const seq = this.liveSeq
      const generation = this.generation
      let historyRequested = false
      const loadOnce = () => {
        if (historyRequested || seq !== this.liveSeq) return
        historyRequested = true
        this.loadHistory(country, seq, generation)
      }
      this.stream = openChatStream(country, generation, {
        onOpen: () => {
          if (seq !== this.liveSeq) return
          this.streamOnline = true
          // first connection: the history is loaded now. After a reconnect: catch up on what was missed
          if (!historyRequested) loadOnce()
          else this.loadHistory(country, seq, generation)
        },
        onMessage: message => { if (seq === this.liveSeq) this.onLive(message) },
        onClose: () => { if (seq === this.liveSeq) this.streamOnline = false }
      })
      // if the stream can not be opened, the history still loads
      this.historyTimer = setTimeout(loadOnce, 3000)
    },
    closeStream () {
      clearTimeout(this.historyTimer)
      if (this.stream) this.stream.close()
      this.stream = null
    },
    // a message from the journey arrived on the stream (it has the exact payload)
    onLive (message) {
      if (!message || !message.msgId || !message.payload) return
      const existing = this.messages.find(m => m.msgId === message.msgId)
      if (existing) {
        // already shown from the history, which only has a rebuilt version: use the exact one
        existing.payload = message.payload
        return
      }
      this.messages.push({ msgId: message.msgId, ts: message.ts || Date.now(), direction: message.direction || 'out', payload: message.payload, status: null })
      if (message.direction !== 'in') this.stopTyping()
      this.scrollToBottom()
    },
    // adds the history messages we do not have yet and puts everything back in time order
    mergeHistory (list) {
      const known = {}
      this.messages.forEach(m => { known[m.msgId] = true })
      const additions = list.filter(m => !known[m.msgId])
      if (!additions.length) return
      this.messages = this.messages
        .concat(additions)
        .map((m, i) => ({ m, i }))
        .sort((a, b) => (a.m.ts - b.m.ts) || (a.i - b.i))
        .map(x => x.m)
      this.scrollToBottom()
    },
    // "typing..." while a reply is expected, it gives up after a while
    startTyping () {
      this.typing = true
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(() => { this.typing = false }, TYPING_MAX_MS)
      this.scrollToBottom()
    },
    stopTyping () {
      this.typing = false
      clearTimeout(this.typingTimer)
    },
    push (direction, payload, status) {
      const message = { msgId: nextId(), ts: Date.now(), direction, payload, status: status || null }
      this.messages.push(message)
      this.scrollToBottom()
      return this.messages[this.messages.length - 1]
    },
    // Posts a user message to the chat service. `body` is what the server expects,
    // `shown` is the payload drawn in the thread. Returns 'blocked' (nothing drawn, for example
    // no country), 'sent' or 'failed'.
    async deliver (body, shown) {
      if (this.demo) {
        this.push('in', shown)
        return 'sent'
      }
      if (!this.selectedCountry) {
        this.sendError = 'Select a country first.'
        return 'blocked'
      }
      this.sendError = ''
      const message = this.push('in', shown, 'sending')
      try {
        const res = await api.buildRequest('chat/message', 'POST', Object.assign({ country: this.selectedCountry.name, generation: this.generation }, body))
        if (res.status === 200) {
          message.status = 'sent'
          // use the id the service logged it under, so the history does not show it twice
          const sentInfo = res.data && res.data.payload
          if (sentInfo && sentInfo.msgId) {
            message.msgId = sentInfo.msgId
            if (sentInfo.ts) message.ts = sentInfo.ts
          }
          this.startTyping()
          return 'sent'
        }
        message.status = 'failed'
        this.sendError = (res.data && res.data.payload && res.data.payload.message) || 'Could not send the message.'
      } catch (e) {
        message.status = 'failed'
        this.sendError = 'Could not send the message.'
      }
      return 'failed'
    },
    async send () {
      if (this.pending) return this.sendAttachment()
      const text = this.draft.trim()
      if (!text) return
      this.emojiOpen = false
      const result = await this.deliver({ type: 'text', text }, { type: 'text', text: { body: text } })
      // keep the text only when nothing was drawn (for example, no country selected)
      if (result !== 'blocked') this.draft = ''
    },
    sendAttachment () {
      if (!this.demo) {
        this.attachError = 'Sending attachments is not supported yet.'
        return
      }
      const p = this.pending
      const caption = this.draft.trim()
      const media = { link: p.url }
      if (caption) media.caption = caption
      if (p.kind === 'document') {
        media.filename = p.name
        media.size = p.size
      }
      this.push('in', { type: p.kind, [p.kind]: media })
      // the object URL stays alive so the bubble can keep showing it
      this.pending = null
      this.draft = ''
    },
    onFilePicked (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      this.attachError = ''
      if (!this.demo) {
        this.attachError = 'Sending attachments is not supported yet.'
        return
      }
      if (file.size > MAX_FILE_BYTES) {
        this.attachError = 'File is too large. Max 16 MB.'
        return
      }
      this.clearPending()
      const kind = file.type.indexOf('image/') === 0 ? 'image' : (file.type.indexOf('video/') === 0 ? 'video' : 'document')
      this.pending = {
        file,
        kind,
        url: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
        sizeLabel: file.size > 1048576 ? (file.size / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(file.size / 1024)) + ' KB'
      }
    },
    clearPending () {
      if (this.pending) URL.revokeObjectURL(this.pending.url)
      this.pending = null
    },
    insertEmoji (e) {
      const el = this.$refs.input
      const start = el && el.selectionStart !== undefined ? el.selectionStart : this.draft.length
      const end = el && el.selectionEnd !== undefined ? el.selectionEnd : this.draft.length
      this.draft = this.draft.slice(0, start) + e + this.draft.slice(end)
      this.$nextTick(() => {
        if (el) {
          el.focus()
          const pos = start + e.length
          el.setSelectionRange(pos, pos)
        }
      })
    },
    closeEmoji () {
      this.emojiOpen = false
    },
    onReply (reply) {
      const key = reply.type === 'list_reply' ? 'list_reply' : 'button_reply'
      const interactive = { type: reply.type, [key]: { id: reply.id, title: reply.title } }
      this.deliver({ type: 'interactive', interactive }, { type: 'interactive', interactive })
    },
    onLocation () {
      if (this.demo) {
        this.deliver({}, { type: 'location', location: { latitude: 12.9716, longitude: 77.5946 } })
        return
      }
      if (!navigator.geolocation) {
        this.sendError = 'This browser cannot share a location.'
        return
      }
      navigator.geolocation.getCurrentPosition(
        pos => {
          const location = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
          this.deliver({ type: 'location', location }, { type: 'location', location })
        },
        () => { this.sendError = 'Location permission was denied.' }
      )
    },
    toggleTheme () {
      this.theme = this.theme === 'dark' ? 'light' : 'dark'
      try { window.localStorage.setItem('chatSimulatorTheme', this.theme) } catch (e) {}
    },
    async restart () {
      this.clearPending()
      this.attachError = ''
      this.sendError = ''
      this.stopTyping()
      this.messages = []
      // clear the journey state as well, the service understands the text "restart"
      if (!this.demo && this.selectedCountry) {
        try {
          const res = await api.buildRequest('chat/message', 'POST', { country: this.selectedCountry.name, generation: this.generation, type: 'text', text: 'restart' })
          if (res.status !== 200) {
            this.sendError = 'Could not restart the chat on the server.'
          }
        } catch (e) {
          this.sendError = 'Could not restart the chat on the server.'
        }
      }
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.thread
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.cs-page { --cs-bg: #0b141a; --cs-panel: #202c33; --cs-field: #2a3942; --cs-out: #202c33; --cs-in: #005c4b; --cs-text: #e9edef; --cs-time: #8696a0; --cs-link: #53bdeb; --cs-action: #21c063; --cs-divider: rgba(134,150,160,.25); --cs-hover: rgba(255,255,255,.05); --cs-doodle: rgba(255,255,255,.035); --cs-scroll: rgba(134,150,160,.35); --cs-scroll-hover: rgba(134,150,160,.6); --cs-outer: #050b0e; --cs-shadow: 0 0 40px rgba(0,0,0,.5);
  position: fixed; top: 0; right: 0; bottom: 0; left: 0; z-index: 1000; display: flex; justify-content: center; align-items: stretch; text-align: left; color: var(--cs-text); background-color: var(--cs-bg);
  background-image: radial-gradient(var(--cs-doodle) 2px, transparent 2px), radial-gradient(var(--cs-doodle) 1.5px, transparent 1.5px);
  background-size: 46px 46px, 31px 31px; background-position: 0 0, 15px 20px; font-family: "Segoe UI", Helvetica Neue, Helvetica, Arial, sans-serif; }
.cs-page[data-theme="light"] { --cs-bg: #efeae2; --cs-panel: #f0f2f5; --cs-field: #ffffff; --cs-out: #ffffff; --cs-in: #d9fdd3; --cs-text: #111b21; --cs-time: #667781; --cs-link: #027eb5; --cs-action: #00a884; --cs-divider: rgba(11,20,26,.1); --cs-hover: rgba(11,20,26,.04); --cs-doodle: rgba(11,20,26,.04); --cs-scroll: rgba(11,20,26,.25); --cs-scroll-hover: rgba(11,20,26,.45); --cs-outer: #d1d7db; --cs-shadow: 0 0 30px rgba(11,20,26,.18); }
.cs-shell { display: flex; flex-direction: column; width: 100%; min-width: 0; background: transparent; }
.cs-header { display: flex; align-items: center; gap: 12px; padding: 10px max(16px, calc((100% - 860px) / 2 + 16px)); background: transparent; border-bottom: 1px solid var(--cs-divider); }
.cs-avatar { width: 40px; height: 40px; border-radius: 50%; background: #6a7175; color: #cfd4d6; display: flex; align-items: center; justify-content: center; font-size: 20px; flex: none; }
.cs-title { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.cs-name { font-size: 16px; line-height: 20px; }
.cs-sub { font-size: 13px; color: var(--cs-time); }
.cs-controls { display: flex; align-items: center; gap: 10px; }
.cs-label { margin: 0; font-weight: normal; font-size: 13px; color: var(--cs-time); }
.cs-select { min-width: 190px; height: 38px; padding: 0 34px 0 14px; border-radius: 10px; border: 1px solid var(--cs-divider); background-color: var(--cs-field); color: var(--cs-text); font-size: 14px; outline: none; cursor: pointer;
  -webkit-appearance: none; -moz-appearance: none; appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' fill='none' stroke='%238696a0' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center; }
.cs-select:focus { border-color: #00a884; }
.cs-select:disabled { opacity: .6; cursor: default; }
.cs-select option { background: var(--cs-panel); color: var(--cs-text); }
.cs-svg { display: block; margin: 0 auto; }
.cs-icon-btn { display: flex; align-items: center; justify-content: center; width: 38px; height: 38px; padding: 0; border: 0; border-radius: 50%; background: transparent; color: var(--cs-time); font-size: 17px; cursor: pointer; }
.cs-icon-btn:hover { background: var(--cs-hover); color: var(--cs-text); }
.cs-error { background: #5c1f1f; color: #ffd6d6; padding: 6px 16px; font-size: 13px; }
.cs-thread { flex: 1; overflow-y: auto; padding: 16px max(16px, calc((100% - 860px) / 2 + 16px));
  scrollbar-width: thin; scrollbar-color: var(--cs-scroll) transparent; }
.cs-thread::-webkit-scrollbar { width: 8px; }
.cs-thread::-webkit-scrollbar-track { background: transparent; }
.cs-thread::-webkit-scrollbar-thumb { background: var(--cs-scroll); border-radius: 4px; }
.cs-thread::-webkit-scrollbar-thumb:hover { background: var(--cs-scroll-hover); }
.cs-notice { padding: 6px max(16px, calc((100% - 860px) / 2 + 16px)); font-size: 12px; color: var(--cs-time); background: var(--cs-hover); text-align: center; }
.cs-empty { text-align: center; color: var(--cs-time); margin-top: 40px; }
.cs-footer { display: flex; align-items: center; gap: 6px; padding: 12px max(16px, calc((100% - 860px) / 2 + 16px)) 16px; background: transparent; }
.cs-footer-icon { width: 38px; height: 38px; display: flex; align-items: center; justify-content: center; color: var(--cs-time); font-size: 20px; flex: none; }
.cs-input { flex: 1; padding: 11px 16px; border: 0; border-radius: 24px; outline: none; font-size: 15px; background: var(--cs-field); color: var(--cs-text); }
.cs-input::placeholder { color: var(--cs-time); }
.cs-send { width: 40px; height: 40px; border: 0; border-radius: 50%; background: transparent; color: var(--cs-time); font-size: 19px; cursor: pointer; flex: none; }
.cs-send:not(:disabled) { color: #00a884; }
.cs-send:disabled { cursor: default; }
.cs-typing { display: inline-flex; gap: 4px; background: var(--cs-out); padding: 11px 13px; border-radius: 8px; border-top-left-radius: 0; margin-top: 4px; }
.cs-typing span { width: 6px; height: 6px; border-radius: 50%; background: var(--cs-time); animation: cs-blink 1.2s infinite; }
.cs-typing span:nth-child(2) { animation-delay: .2s; }
.cs-typing span:nth-child(3) { animation-delay: .4s; }
@keyframes cs-blink { 0%, 80%, 100% { opacity: .3; } 40% { opacity: 1; } }
@media (max-width: 700px) { .cs-thread { padding: 10px 10px; } .cs-header { padding: 8px 10px; gap: 8px; } .cs-footer { padding: 8px 10px; } .cs-footer-icon { width: 30px; } .cs-label { display: none; } .cs-select { min-width: 130px; } }
.cs-file { display: none; }
.cs-footer-icon { border: 0; background: transparent; cursor: pointer; border-radius: 50%; }
.cs-footer-icon:hover { background: var(--cs-hover); color: var(--cs-text); }
.cs-pending { display: flex; align-items: center; gap: 12px; margin: 0 max(16px, calc((100% - 860px) / 2 + 16px)); padding: 10px 12px; background: var(--cs-panel); border-radius: 12px 12px 0 0; border-bottom: 1px solid var(--cs-divider); }
.cs-pending-thumb { width: 56px; height: 56px; object-fit: cover; border-radius: 8px; background: #000; }
.cs-pending-icon { font-size: 36px; color: var(--cs-time); width: 56px; text-align: center; }
.cs-pending-meta { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.cs-pending-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cs-pending-meta small { color: var(--cs-time); }
.cs-emoji { display: grid; grid-template-columns: repeat(10, 1fr); gap: 2px; margin: 0 max(16px, calc((100% - 860px) / 2 + 16px)) 4px; max-width: 420px; padding: 8px; background: var(--cs-panel); border-radius: 12px; border: 1px solid var(--cs-divider); }
.cs-emoji-btn { border: 0; background: transparent; font-size: 22px; line-height: 1; padding: 5px 0; border-radius: 6px; cursor: pointer; }
.cs-emoji-btn:hover { background: var(--cs-hover); }
@media (max-width: 700px) { .cs-pending, .cs-emoji { margin-left: 10px; margin-right: 10px; } .cs-emoji { grid-template-columns: repeat(8, 1fr); } }
</style>



// WEBPACK FOOTER //
// ChatSimulator.vue?56e42cc1